import { useCallback, useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import {
  Activity,
  ArrowLeft,
  BadgeCheck,
  ChartNoAxesCombined,
  Clock3,
  HeartPulse,
  Inbox,
  Network,
  Pencil,
  RefreshCw,
  Send,
  ShieldCheck,
  UserRound,
} from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { api } from '../api';
import { useAuth } from '../context/AuthContext';
import { EmptyState, InfoRow, Modal, PageHeader, StatusBadge, Surface } from '../components/ui';
import DeviceLogo, { DeviceHardwareLabel } from '../components/DeviceLogo';

const ONLINE_MS = 2 * 60 * 1000;

function asUtcDate(value) {
  if (!value) return null;
  return new Date(/[zZ]|[+-]\d\d:\d\d$/.test(value) ? value : `${value}Z`);
}

function isOnline(lastSeen) {
  const date = asUtcDate(lastSeen);
  return date && Date.now() - date.getTime() < ONLINE_MS;
}

function formatDate(value) {
  const date = asUtcDate(value);
  return date ? date.toLocaleString() : '—';
}

function formatLastSeen(lastSeen, t) {
  const date = asUtcDate(lastSeen);
  if (!date) return t('deviceDetail.never');
  const diffMinutes = Math.max(0, Math.floor((Date.now() - date.getTime()) / 60_000));
  if (diffMinutes < 1) return t('deviceDetail.justNow');
  if (diffMinutes < 60) return t('deviceDetail.minAgo', { n: diffMinutes });
  const hours = Math.floor(diffMinutes / 60);
  return hours < 24 ? t('deviceDetail.hAgo', { n: hours }) : date.toLocaleString();
}

function EventTypeBadge({ type }) {
  const variants = {
    heartbeat: { icon: HeartPulse, tone: 'success' },
    telemetry: { icon: ChartNoAxesCombined, tone: 'accent' },
    cmd_ack: { icon: BadgeCheck, tone: 'success' },
    command_sent: { icon: Send, tone: 'warning' },
  };
  const variant = variants[type] || { icon: Activity, tone: 'muted' };
  const Icon = variant.icon;
  return (
    <StatusBadge tone={variant.tone} dot={false}>
      <Icon className="icon" aria-hidden="true" />
      {type}
    </StatusBadge>
  );
}

export default function DeviceDetailPage({ backTo = '/' }) {
  const { id } = useParams();
  const { t } = useTranslation();
  const { user } = useAuth();
  const [device, setDevice] = useState(null);
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [openingUi, setOpeningUi] = useState(false);
  const [uiError, setUiError] = useState('');
  const [renaming, setRenaming] = useState(false);
  const [renameName, setRenameName] = useState('');
  const [renameError, setRenameError] = useState('');
  const [renameSaving, setRenameSaving] = useState(false);

  const loadDevice = useCallback(async () => {
    try {
      const data = await api.getDevice(id);
      setDevice(data);
      setError('');
    } catch (err) {
      if (err.status === 403) setError(t('deviceDetail.accessDenied'));
      else if (err.status === 404) setError(t('deviceDetail.notFound'));
      else setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [id, t]);

  const loadEvents = useCallback(() => {
    api.getDeviceEvents(id, 20).then(setEvents).catch(() => {});
  }, [id]);

  useEffect(() => {
    loadDevice();
    loadEvents();
    const timer = setInterval(() => {
      loadDevice();
      loadEvents();
    }, 30_000);
    return () => clearInterval(timer);
  }, [loadDevice, loadEvents]);

  const openRename = () => {
    setRenameName(device?.name || '');
    setRenameError('');
    setRenaming(true);
  };

  const handleRename = async (event) => {
    event.preventDefault();
    setRenameSaving(true);
    setRenameError('');
    try {
      await api.updateDevice(device.id, { name: renameName });
      setRenaming(false);
      await loadDevice();
    } catch (err) {
      setRenameError(err.message);
    } finally {
      setRenameSaving(false);
    }
  };

  if (loading) {
    return <div className="page loading-page"><div className="spinner-border" role="status" aria-label={t('adminSettings.loading')} /></div>;
  }

  if (error || !device) {
    return (
      <div className="page page-narrow">
        <Link to={backTo} className="btn btn-outline-secondary mb-3"><ArrowLeft className="icon" aria-hidden="true" />{t('deviceDetail.back')}</Link>
        <div className="alert alert-danger" role="alert">{error || t('deviceDetail.notFound')}</div>
      </div>
    );
  }

  const online = device.status === 'provisioned' && (device.connection_status
    ? device.connection_status === 'connected' : isOnline(device.last_seen));
  const pending = device.status !== 'provisioned';
  const statusTone = pending ? 'warning' : online ? 'success' : 'muted';
  const statusLabel = pending
    ? t('deviceDetail.statusPending')
    : online ? t('deviceDetail.statusOnline') : t('deviceDetail.statusOffline');
  const mayOpenDeviceUi = device.status === 'provisioned' && (user?.is_admin || (user?.id != null && user.id === device.owner_id));
  const openDeviceUi = async () => {
    setOpeningUi(true);
    setUiError('');
    // Reserve the tab while still inside the user gesture, then navigate it to
    // the separate device origin after receiving a short-lived launch URL.
    const tab = window.open('about:blank', '_blank');
    if (tab) tab.opener = null;
    try {
      const session = await api.createDeviceUiSession(id);
      if (!session?.url) throw new Error(t('errors.serviceUnavailable'));
      if (tab) tab.location.replace(session.url);
      else window.location.assign(session.url);
    } catch (err) {
      if (tab) tab.close();
      setUiError(err.message);
    } finally {
      setOpeningUi(false);
    }
  };

  return (
    <div className="page">
      <Link to={backTo} className="back-link"><ArrowLeft className="icon" aria-hidden="true" />{t('deviceDetail.back')}</Link>
      <PageHeader
        leading={<DeviceLogo device={device} size={48} />}
        title={device.name || t('deviceDetail.unnamed')}
        subtitle={<><DeviceHardwareLabel device={device} /><code className="d-block mt-1 text-break">{device.serial_number}</code></>}
        actions={(
          <>
            <StatusBadge tone={statusTone}>{statusLabel}</StatusBadge>
            <button type="button" className="btn btn-outline-secondary" onClick={openRename}><Pencil className="icon" aria-hidden="true" />{t('deviceDetail.rename')}</button>
            {mayOpenDeviceUi && (
              <button
                type="button"
                className="btn btn-primary"
                onClick={openDeviceUi}
                disabled={openingUi}
              >
                <Network className="icon" aria-hidden="true" />{t('deviceDetail.openUi')}
              </button>
            )}
          </>
        )}
      />
      {uiError && <div className="alert alert-danger" role="alert">{uiError}</div>}

      <div className="content-grid device-detail-grid mb-4">
        <Surface className="grid-span-4 device-detail-info" title={t('deviceDetail.cardInfo')} icon={ShieldCheck}>
          <div className="surface-body p-0">
            <InfoRow label={t('deviceDetail.fieldName')}>{device.name || t('deviceDetail.unnamed')}</InfoRow>
            <InfoRow label={t('deviceDetail.fieldSerial')}><code>{device.serial_number}</code></InfoRow>
            <InfoRow label={t('deviceDetail.fieldLastSeen')}><span className={`tone-text-${statusTone}`}>{formatLastSeen(device.last_seen, t)}</span></InfoRow>
            <InfoRow label={t('deviceDetail.fieldRegistered')}>{formatDate(device.created_at)}</InfoRow>
            {device.provisioned_at && <InfoRow label={t('deviceDetail.fieldProvisioned')}>{formatDate(device.provisioned_at)}</InfoRow>}
            {device.notes && <InfoRow label={t('deviceDetail.fieldNotes')}><span className="inline-icon"><Clock3 className="icon" aria-hidden="true" />{device.notes}</span></InfoRow>}
            {device.owner_username && <InfoRow label={t('deviceDetail.fieldOwner')}><span className="inline-icon"><UserRound className="icon" aria-hidden="true" />{device.owner_username}</span></InfoRow>}
          </div>
        </Surface>

        {device.status === 'provisioned' ? (
          <Surface
            className="grid-span-8 device-detail-config"
            title={t('deviceDetail.cardConnection')}
            icon={Network}
          >
            <div className="surface-body p-0">
              <InfoRow label={t('deviceDetail.fieldTransport')}><code>{device.transport || '—'}</code></InfoRow>
              <InfoRow label={t('deviceDetail.fieldEndpoint')}><code>{device.grpc_endpoint || '—'}</code></InfoRow>
              <InfoRow label={t('deviceDetail.fieldConnectionStatus')}>{device.connection_status || '—'}</InfoRow>
              <InfoRow label={t('deviceDetail.fieldCertificateExpires')}>{formatDate(device.certificate_expires_at)}</InfoRow>
              <p className="form-text p-3 mb-0">{t('deviceDetail.connectionNote')}</p>
            </div>
          </Surface>
        ) : (
          <Surface className="grid-span-8 device-detail-config" title={t('deviceDetail.cardProvision')} icon={Network}>
            <EmptyState icon={Clock3} title={t('deviceDetail.notProvisioned')} hint={t('deviceDetail.provisionHint')} />
          </Surface>
        )}
      </div>

      <Surface
        title={t('deviceDetail.cardEvents')}
        icon={Activity}
        action={<button type="button" className="icon-btn" onClick={loadEvents} title={t('deviceDetail.eventsRefresh')} aria-label={t('deviceDetail.eventsRefresh')}><RefreshCw className="icon" aria-hidden="true" /></button>}
      >
        {events.length === 0 ? <EmptyState icon={Inbox} title={t('deviceDetail.eventsEmpty')} /> : (
          <div className="table-responsive">
            <table className="table table-hover mb-0">
              <thead><tr><th>{t('deviceDetail.eventsColTime')}</th><th>{t('deviceDetail.eventsColType')}</th><th>{t('deviceDetail.eventsColData')}</th></tr></thead>
              <tbody>{events.map((event) => (
                <tr key={event.id}>
                  <td className="small text-muted text-nowrap">{formatDate(event.created_at)}</td>
                  <td><EventTypeBadge type={event.event_type} /></td>
                  <td>{event.payload && Object.keys(event.payload).length > 0 ? <code className="event-payload">{JSON.stringify(event.payload)}</code> : <span className="text-muted">—</span>}</td>
                </tr>
              ))}</tbody>
            </table>
          </div>
        )}
      </Surface>

      {renaming && (
        <Modal title={t('deviceDetail.rename')} onClose={() => setRenaming(false)} labelledBy="rename-device-title">
          <p className="small text-muted font-monospace text-break">{device.serial_number}</p>
          {renameError && <div className="alert alert-danger py-2 small" role="alert">{renameError}</div>}
          <form onSubmit={handleRename}>
            <label className="form-label" htmlFor="device-detail-name">{t('deviceDetail.fieldName')}</label>
            <input id="device-detail-name" className="form-control" value={renameName} onChange={(event) => setRenameName(event.target.value)} placeholder={t('deviceDetail.renamePlaceholder')} autoFocus />
            <div className="modal-actions">
              <button type="button" className="btn btn-outline-secondary" onClick={() => setRenaming(false)}>{t('deviceDetail.renameCancel')}</button>
              <button type="submit" className="btn btn-primary" disabled={renameSaving}>{renameSaving ? t('profile.saving') : t('deviceDetail.renameSave')}</button>
            </div>
          </form>
        </Modal>
      )}
    </div>
  );
}
